/**
 * Drawer-Tab "Verlauf": chronologische Activity-Log-Einträge für DIESEN Investor.
 * Filter: Akteur. Read-only.
 */
import React, { useEffect, useState, useMemo } from "react";
import { api } from "../../lib/api";
import { Activity, Filter } from "lucide-react";

const AKT_LABELS = {
  kontakthistorie_seed: "Kontakt-Seed",
  kontakthistorie_neu: "Kontakt-Eintrag",
  investor_phase_change: "Phase geändert",
  investor_update: "Investor geändert",
  investor_sperre: "Sperre",
  investor_dokument_status: "Dokument-Status",
  investor_datenraum: "Datenraum",
  dokument_upload: "Dokument hochgeladen",
  dokument_delete: "Dokument entfernt",
  bulk_update: "Bulk-Update",
  nda_reminder_sent: "NDA-Erinnerung",
  investor_einstufung_change: "Einstufung geändert",
  investor_welle_change: "Welle geändert",
  investor_betreuer_change: "Betreuer geändert",
  datenraum_einladung_versandt: "Datenraum-Einladung versandt",
};

const fmtTs = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

export default function VerlaufTab({ investorId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAkteur, setFilterAkteur] = useState("");

  useEffect(() => {
    if (!investorId) return;
    setLoading(true);
    api
      .get("/activity-log", {
        params: { ressource_id: investorId, page: 1, page_size: 200 },
      })
      .then(({ data }) => setItems(data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [investorId]);

  const akteure = useMemo(() => {
    const s = new Set(items.map((i) => i.akteur).filter(Boolean));
    return Array.from(s).sort();
  }, [items]);

  const filtered = useMemo(() => {
    if (!filterAkteur) return items;
    return items.filter((i) => i.akteur === filterAkteur);
  }, [items, filterAkteur]);

  return (
    <section data-testid="drawer-verlauf-tab" className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="pb-eyebrow flex items-center gap-2">
          <Activity size={11} /> Verlauf
          <span className="text-[10px] text-navy/40 tracking-normal normal-case font-normal">
            Lückenlose Dokumentation aller Änderungen
          </span>
        </div>
        {akteure.length > 1 && (
          <div className="flex items-center gap-2">
            <Filter size={12} className="text-navy/40"/>
            <select
              data-testid="verlauf-filter-akteur"
              value={filterAkteur}
              onChange={(e) => setFilterAkteur(e.target.value)}
              className="text-xs font-sans bg-transparent border border-navy/20 px-2 py-1"
            >
              <option value="">Alle Akteure</option>
              {akteure.map((a) => <option key={a}>{a}</option>)}
            </select>
          </div>
        )}
      </div>

      {loading ? (
        <div className="pb-placeholder">Lade Verlauf …</div>
      ) : filtered.length === 0 ? (
        <div className="pb-placeholder">Keine Einträge.</div>
      ) : (
        <ul className="border border-navy/15 divide-y divide-navy/10" data-testid="verlauf-list">
          {filtered.map((a) => {
            const detail = Object.entries(a.details || {})[0];
            return (
              <li key={a.id} className="px-4 py-3 text-sm font-sans">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-navy font-medium">
                      {AKT_LABELS[a.aktion_typ] || a.aktion_typ}
                    </div>
                    {detail && (
                      <div className="text-xs text-navy/55 mt-0.5 truncate">
                        <strong>{detail[0]}:</strong>{" "}
                        {typeof detail[1] === "object" ? JSON.stringify(detail[1]) : String(detail[1])}
                      </div>
                    )}
                  </div>
                  <div className="text-right whitespace-nowrap">
                    <div className="text-[10px] uppercase tracking-widish px-2 py-0.5 bg-ledger border border-navy/20 text-navy/80 inline-block">
                      {a.akteur}
                    </div>
                    <div className="text-[11px] text-navy/55 tabular-nums mt-0.5">{fmtTs(a.timestamp)}</div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
